import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lvc-live-classes/artifacts/op54e7uq_WhatsApp%20Image%202026-04-08%20at%2013.06.26.jpeg";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'teacher') navigate('/teacher');
      else navigate('/student');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1768522036802-850bed49bd8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGJ1cmd1bmR5JTIwdGV4dHVyZXxlbnwwfHx8fDE3NzU2NjU2MzB8MA&ixlib=rb-4.1.0&q=85"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#8B1538]/90 to-[#8B1538]/70" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <img src={LOGO_URL} alt="LVC Misiones" className="h-20 w-20 rounded-full object-cover mb-8" />
          <h1 className="font-heading text-4xl lg:text-5xl font-light mb-4">
            Bienvenido de Nuevo
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Continúa tu formación misionera y conecta con nuestra comunidad de aprendizaje.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="LVC Misiones" className="h-12 w-12 rounded-full object-cover" />
            <span className="font-heading text-xl font-semibold text-[#8B1538]">LVC Misiones</span>
          </Link>

          <Card className="border border-[#E5DFD3] shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="font-heading text-2xl text-[#1A1A1A]">Iniciar Sesión</CardTitle>
              <CardDescription className="text-[#737373]">
                Ingresa tus credenciales para acceder
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-[#9B2226]/10 border-[#9B2226]/20 text-[#9B2226]">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#4A4A4A]">Correo Electrónico</Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-[#E5DFD3] focus:ring-[#8B1538]/20 focus:border-[#8B1538]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#4A4A4A]">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="border-[#E5DFD3] focus:ring-[#8B1538]/20 focus:border-[#8B1538] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#4A4A4A]"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col gap-4">
                <Button 
                  type="submit"
                  data-testid="login-submit-btn"
                  className="w-full bg-[#8B1538] hover:bg-[#70102B] text-white"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Ingresando...
                    </>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </Button>
                
                <p className="text-sm text-center text-[#737373]">
                  ¿No tienes una cuenta?{' '}
                  <Link to="/register" className="text-[#8B1538] hover:underline font-medium">
                    Regístrate
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
